import React, { useCallback, useEffect, useRef, useState } from "react";
import { CalendarDays } from "lucide-react";
import { get, todayISO, loadVisit, visitKey, queue, pending } from "./techData";
import { Screen, Card } from "./ui";
import { outcomeTag } from "../components/software/reminders/boardUtils";

const Finished = ({ j, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-end" onClick={onClose} data-testid="tech-finished-popup">
    <div className="w-full bg-[#f2f5f9] rounded-t-3xl p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
      <div className="text-[20px] font-black text-[#0f2f52]">{label(j)}</div>
      <Card className="space-y-1">
        <div className="text-[17px] font-black text-slate-700">✓ {outcomeTag(j)}{j.done_at ? ` · ${j.done_at.slice(0, 10).split("-").reverse().join("/")}` : ""}{j.done_by ? ` · ${j.done_by}` : ""}</div>
        {j.wo && <div className="text-[15px] font-bold text-slate-600">W/O {j.wo}</div>}
        <div className="text-[15px] text-slate-700 whitespace-pre-wrap">{j.text}</div>
      </Card>
      {j.wo_result && <Card className="bg-green-50 border-2 border-green-300 text-[15px] text-green-900 whitespace-pre-wrap" data-testid="tech-finished-result">{j.wo_result}</Card>}
      <div className="text-center text-[14px] text-slate-500">Finished jobs stay on the board as history and can't be changed.</div>
      <button onClick={onClose} data-testid="tech-finished-close" className="w-full h-14 rounded-2xl bg-[#0f2f52] text-white text-[18px] font-black">Close</button>
    </div>
  </div>
);

const addDays = (iso, n) => { const d = new Date(iso + "T12:00:00"); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const dayName = (iso) => new Date(iso + "T12:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "short" });
const isDone = (j) => j.visit === "done" || loadVisit(visitKey(j, j.client_id, j.date))?.status === "done";
const monday = (iso) => { const w = new Date(iso + "T12:00:00").getDay(); return w === 6 ? addDays(iso, 2) : w === 0 ? addDays(iso, 1) : iso; }; // board has no weekends
const label = (j) => `${j.client || j.text}${j.suburb ? ` - ${j.suburb.toUpperCase()}` : ""}`;

// one White Board magnet: Name Surname - SUBURB; hold & drag to reorder or move day
function Magnet({ j, onOpen, onDragStart, onDropOn }) {
  const done = isDone(j), fixed = !!j.visit_id;
  if (j.finished) return ( // finished: stays on the run as history, locked, tap to view
    <div role="button" tabIndex={0} onClick={onOpen} data-testid={`tech-job-${j.id}`}
      className="w-full min-h-[56px] rounded-full px-5 py-2 flex flex-col items-center justify-center text-center bg-slate-200 text-slate-500 leading-tight select-none cursor-pointer">
      <span className="line-clamp-1 break-words text-[19px] font-medium" data-testid={`tech-job-label-${j.id}`}>✓ {label(j)}</span>
      <span className="text-[12px] font-black uppercase tracking-wide" data-testid={`tech-job-outcome-${j.id}`}>{outcomeTag(j)}</span>
    </div>
  );
  return (
    <div role="button" tabIndex={0} onClick={onOpen} data-testid={`tech-job-${j.id}`}
      draggable={!fixed} onDragStart={(e) => { e.dataTransfer.setData("text/plain", j.id); onDragStart(j); }}
      onDragEnter={(e) => e.preventDefault()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDropOn(j); }}
      className={`w-full min-h-[56px] rounded-full px-5 py-2 flex items-center justify-center text-center bg-[#8BC34A] text-slate-900 text-[21px] font-medium leading-tight shadow-sm select-none cursor-pointer active:scale-[0.99] transition-transform ${done ? "opacity-60" : ""}`}>
      <span className="line-clamp-2 break-words" data-testid={`tech-job-label-${j.id}`}>{done && "✓ "}{label(j)}</span>
    </div>
  );
}

export default function RunScreen({ nav, tabs }) {
  const [when, setWhen] = useState("today"), [d, setD] = useState(null), [err, setErr] = useState(""), [drag, setDrag] = useState(null), [pick, setPick] = useState(null);
  const t = todayISO(), dragging = useRef(false);
  const days = when === "week" ? [...Array(7)].map((_, i) => addDays(t, i)).filter((x) => monday(x) === x) : [when === "today" ? t : addDays(t, 1)];
  const load = useCallback(() => {
    const q = when === "week" ? `date=${t}&days=7` : `date=${when === "today" ? t : addDays(t, 1)}&days=1`;
    get(`/tech/day?${q}`).then((x) => { if (!dragging.current) { setD(x); setErr(""); } }).catch((e) => setErr(e.message));
  }, [when, t]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { // same board as the office: keep it fresh
    const f = () => { if (document.visibilityState === "visible" && !dragging.current) pending().then((n) => { if (!n) load(); }).catch(() => {}); };
    const iv = setInterval(f, 6000); document.addEventListener("visibilitychange", f);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", f); };
  }, [load]);
  const jobs = d?.jobs || [];
  const [info, setInfo] = useState(null);
  const open = (j) => (j.finished ? setInfo(j) : nav.go("stop", { job: j, clientId: j.client_id, date: j.date }));
  const start = (j) => { dragging.current = true; setDrag(j); };
  const end = () => { dragging.current = false; setDrag(null); };
  // put magnet on `day` before `before` (or at the end); writes straight to the office White Board
  const place = (j, to, before) => {
    end();
    const day = monday(to);
    if (!j || j.finished || (before && before.id === j.id)) return;
    if (before?.finished) before = null;
    const rest = jobs.filter((x) => x.id !== j.id), moved = { ...j, date: day };
    const list = rest.filter((x) => x.date === day);
    const at = before ? list.findIndex((x) => x.id === before.id) : list.length;
    list.splice(at < 0 ? list.length : at, 0, moved);
    if (j.date !== day) queue(`move:${j.id}`, "PATCH", `/reminders/${j.id}?due_date=${day}&on_board=true`, {});
    list.forEach((x, i) => { if (!x.visit_id && !x.finished) queue(`seq:${x.id}`, "PATCH", `/reminders/${x.id}?seq=${i + 1}`, {}); });
    const shown = days.includes(day);
    setD((o) => ({ ...o, jobs: [...rest.filter((x) => x.date !== day), ...(shown ? list.map((x, i) => ({ ...x, seq: i + 1 })) : [])].sort((a, b) => (a.date + String(a.seq).padStart(4, "0")).localeCompare(b.date + String(b.seq).padStart(4, "0"))),
      overdue: (o.overdue || []).filter((x) => x.id !== j.id) }));
  };
  const zone = (fn) => ({ onDragEnter: (e) => e.preventDefault(), onDragOver: (e) => e.preventDefault(), onDrop: (e) => { e.preventDefault(); fn(); } });
  return (
    <Screen title={`Hi ${(nav.user.name || "").split(" ")[0]}`} sub={dayName(t)} testid="tech-run" right={<div className="space-y-1">
      <div className="grid grid-cols-3 gap-1" data-testid="tech-run-when">
        {[["today", "Today"], ["tomorrow", "Tomorrow"], ["week", "Week"]].map(([k, l]) => (
          <button key={k} onClick={() => setWhen(k)} data-testid={`tech-run-${k}`} className={`h-9 px-0 rounded-md text-[14px] tracking-tight font-black text-yellow-300 border-2 ${when === k ? "bg-[#1583d6] border-yellow-300" : "bg-[#1e9be9] border-[#1e9be9]"}`}>{l}</button>
        ))}
      </div>
      {tabs}
    </div>}>
      {err && <Card className="text-red-700 font-bold">{err}</Card>}
      {!d && !err && <Card className="text-center text-slate-500 text-[17px]">Loading your run…</Card>}
      {d && days.map((day) => {
        const list = jobs.filter((j) => j.date === day);
        return (
          <div key={day} className="space-y-3 pb-3" data-testid={`tech-run-day-${day}`} onDragEnd={end} {...zone(() => place(drag, day))}>
            <div data-testid={`tech-run-dayhead-${day}`} className={`text-[15px] font-black uppercase tracking-wide pt-2 pb-1 px-1 rounded-lg ${drag ? "bg-sky-100 text-[#0f2f52]" : "text-slate-500"}`}>{dayName(day)} · {list.length}</div>
            {list.map((j) => <Magnet key={j.id} j={j} onOpen={() => open(j)} onDragStart={start} onDropOn={(b) => place(drag, day, b)} />)}
            {!list.length && when === "week" && <div className="h-12 rounded-full border-2 border-dashed border-slate-300 text-center text-slate-500 text-[15px] font-bold flex items-center justify-center">Nothing booked</div>}
            {!list.length && when !== "week" && <Card className="text-center text-slate-500 text-[17px] py-8" data-testid="tech-run-empty">Nothing on the board {when}.</Card>}
          </div>
        );
      })}
      {d?.overdue?.length > 0 && when === "today" && (
        <div className="space-y-3 pt-2" onDragEnd={end}>
          <div className="text-[15px] font-black text-red-600 uppercase tracking-wide">Overdue · {d.overdue.length}</div>
          {d.overdue.map((j) => <Magnet key={j.id} j={j} onOpen={() => open({ ...j, date: t })} onDragStart={start} onDropOn={() => {}} />)}
        </div>
      )}
      {drag && <div {...zone(() => { setPick(drag); end(); })} data-testid="tech-run-move-date" className="fixed left-3 right-3 bottom-4 z-50 h-16 rounded-2xl bg-[#0f2f52] text-white text-[18px] font-black flex items-center justify-center gap-2 shadow-lg"><CalendarDays className="h-6 w-6" />Drop here to pick another date</div>}
      {pick && (
        <Card className="space-y-3 border-2 border-[#0f2f52]" data-testid="tech-run-date-panel">
          <div className="text-[18px] font-black">Move {label(pick)} to:</div>
          <div className="text-[14px] text-slate-600">Saturday or Sunday moves to the Monday.</div>
          <input type="date" min={t} defaultValue={pick.date} data-testid="tech-run-date-input" className="w-full h-14 rounded-xl border-2 border-slate-300 px-3 text-[18px]"
            onChange={(e) => { if (e.target.value) { place(pick, e.target.value); setPick(null); } }} />
          <button onClick={() => setPick(null)} data-testid="tech-run-date-cancel" className="w-full h-12 rounded-xl bg-slate-100 font-black text-slate-700">Cancel</button>
        </Card>
      )}
      {info && <Finished j={info} onClose={() => setInfo(null)} />}
      {d && jobs.length > 0 && !drag && <div className="text-center text-[14px] text-slate-600 pt-2">Hold a magnet, then drag it to change the order or the day</div>}
    </Screen>
  );
}
